import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Settings2, 
  Send, 
  Loader2,
  CheckCircle,
  History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../App';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImportAlertSettings({ module = 'ventes', trigger }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  const moduleLabel = module === 'ventes' ? 'Ventes (Factures Clients)' : 'Achats (Factures Fournisseurs)';
  const moduleColor = module === 'ventes' ? 'blue' : 'orange';
  
  const [settings, setSettings] = useState({
    sender_email: `imports-${module}@betaye.fr`,
    recipient_email: '',
    cc_email: '',
    enabled: false,
    min_failures_to_alert: 1,
    include_success_summary: true
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/import-alerts/settings/${module}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/api/import-alerts/history/${module}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.history || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchHistory();
    }
  }, [open, token, module]);

  const handleSave = async () => {
    if (!settings.sender_email.endsWith('@betaye.fr')) {
      toast.error("L'adresse d'envoi doit se terminer par @betaye.fr");
      return;
    }
    
    if (settings.enabled && !settings.recipient_email) {
      toast.error("Veuillez renseigner une adresse de destination");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/api/import-alerts/settings/${module}`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Paramètres ${moduleLabel} enregistrés`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!settings.recipient_email) {
      toast.error("Veuillez d'abord configurer un destinataire");
      return;
    }

    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/api/import-alerts/test/${module}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success('Email de test envoyé !');
        fetchHistory();
      } else {
        toast.error(res.data.message || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi du test");
    } finally {
      setSendingTest(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Settings2 className="w-4 h-4 mr-2" />
            Alertes email
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className={`w-5 h-5 text-${moduleColor}-600`} />
            Alertes email - {moduleLabel}
          </DialogTitle>
          <DialogDescription>
            Recevez un email récapitulatif lorsqu'un import en masse rencontre des échecs
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <Tabs defaultValue="settings" className="w-full">
            <TabsList className="w-full mb-4">
              <TabsTrigger value="settings" className="flex-1">
                <Settings2 className="w-4 h-4 mr-2" />
                Paramètres
              </TabsTrigger>
              <TabsTrigger value="history" className="flex-1">
                <History className="w-4 h-4 mr-2" />
                Historique
              </TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="space-y-6">
              {/* Module indicator */}
              <div className={`p-3 rounded-lg border ${module === 'ventes' ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                <p className={`text-sm font-medium ${module === 'ventes' ? 'text-blue-700' : 'text-orange-700'}`}>
                  Configuration spécifique au module {module === 'ventes' ? 'Ventes' : 'Achats'}
                </p>
              </div>
              
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                <div>
                  <Label className="text-base">Activer les alertes</Label>
                  <p className="text-sm text-muted-foreground">
                    Envoyer un email lorsqu'un import échoue partiellement
                  </p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                />
              </div>

              {/* Sender Email */}
              <div className="space-y-2">
                <Label htmlFor="sender_email">Adresse d'envoi</Label>
                <Input
                  id="sender_email"
                  type="email"
                  value={settings.sender_email}
                  onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                  placeholder={`imports-${module}@betaye.fr`}
                />
                <p className="text-xs text-muted-foreground">
                  Doit se terminer par @betaye.fr
                </p>
              </div>

              {/* Recipient Email */}
              <div className="space-y-2">
                <Label htmlFor="recipient_email">Destinataire *</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={settings.recipient_email}
                  onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
                  placeholder="votre.email@exemple.com"
                />
              </div>

              {/* CC Email */}
              <div className="space-y-2">
                <Label htmlFor="cc_email">Copie (CC)</Label>
                <Input
                  id="cc_email"
                  type="email"
                  value={settings.cc_email || ''}
                  onChange={(e) => setSettings({ ...settings, cc_email: e.target.value })}
                  placeholder="autre.email@exemple.com (optionnel)"
                />
              </div>

              {/* Minimum failures */}
              <div className="space-y-2">
                <Label htmlFor="min_failures">Seuil minimum d'échecs</Label>
                <Input
                  id="min_failures"
                  type="number"
                  min="1"
                  max="100"
                  value={settings.min_failures_to_alert}
                  onChange={(e) => setSettings({ ...settings, min_failures_to_alert: parseInt(e.target.value) || 1 })}
                />
                <p className="text-xs text-muted-foreground">
                  Nombre minimum d'échecs pour déclencher une alerte
                </p>
              </div>

              {/* Include success summary */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label>Inclure le récapitulatif des succès</Label>
                  <p className="text-sm text-muted-foreground">
                    Afficher aussi les fichiers importés avec succès
                  </p>
                </div>
                <Switch
                  checked={settings.include_success_summary}
                  onCheckedChange={(checked) => setSettings({ ...settings, include_success_summary: checked })}
                />
              </div>

              {/* Test Button */}
              <div className={`p-4 border rounded-lg ${module === 'ventes' ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${module === 'ventes' ? 'text-blue-900' : 'text-orange-900'}`}>Tester la configuration</p>
                    <p className={`text-sm ${module === 'ventes' ? 'text-blue-700' : 'text-orange-700'}`}>
                      Envoyer un email de test avec des données fictives
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleTest}
                    disabled={sendingTest || !settings.recipient_email}
                    className={module === 'ventes' ? 'border-blue-300 text-blue-700 hover:bg-blue-100' : 'border-orange-300 text-orange-700 hover:bg-orange-100'}
                  >
                    {sendingTest ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Envoyer un test
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="history">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune alerte envoyée pour {module === 'ventes' ? 'Ventes' : 'Achats'}</p>
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Résultat</TableHead>
                        <TableHead>Par</TableHead>
                        <TableHead>Destinataire</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateTime(item.sent_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-green-600">{item.successful} ✓</span>
                              <span className="text-red-600">{item.failed} ✗</span>
                              {item.duplicates > 0 && (
                                <span className="text-amber-600">{item.duplicates} ⚠</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.user_email}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.recipient}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
